// Auto-translates any visible text on the page to the current i18n language.
// Scans the DOM every second, detects untranslated text nodes, batches them,
// sends them to the translate-batch edge function, and swaps the DOM in place.
// When the user switches back to the source language (Arabic), it restores
// the original strings.
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

const SOURCE_LANG = "ar"; // strings in the codebase are Arabic by default
const SCAN_INTERVAL_MS = 1000;
const MAX_BATCH = 40;

// Per-language cache: original text -> translated text
const cache: Map<string, Map<string, string>> = new Map();
// In-flight requests to avoid duplicate calls for the same string
const inFlight: Map<string, Set<string>> = new Map();

// WeakMap tying a text node to its ORIGINAL Arabic content
const originals: WeakMap<Text, string> = new WeakMap();

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT", "SVG", "PATH",
]);

function shouldSkipNode(node: Text): boolean {
  const parent = node.parentElement;
  if (!parent) return true;
  if (SKIP_TAGS.has(parent.tagName)) return true;
  if (parent.closest("[data-no-translate]")) return true;
  if (parent.closest("[contenteditable='true']")) return true;
  const raw = node.nodeValue || "";
  const trimmed = raw.trim();
  if (trimmed.length === 0) return true;
  // Skip pure numbers / addresses / URLs
  if (/^[\d\s.,:+\-()%$€£¥/]+$/.test(trimmed)) return true;
  if (/^0x[a-fA-F0-9]{6,}$/.test(trimmed)) return true;
  if (/^(https?:\/\/|www\.)/.test(trimmed)) return true;
  return false;
}

function collectTextNodes(root: Node): Text[] {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const t = n as Text;
    if (!shouldSkipNode(t)) nodes.push(t);
  }
  return nodes;
}

function getCache(lang: string): Map<string, string> {
  let m = cache.get(lang);
  if (!m) { m = new Map(); cache.set(lang, m); }
  return m;
}

function getInFlight(lang: string): Set<string> {
  let s = inFlight.get(lang);
  if (!s) { s = new Set(); inFlight.set(lang, s); }
  return s;
}

async function translateBatch(texts: string[], target: string): Promise<string[]> {
  const { data, error } = await supabase.functions.invoke("translate-batch", {
    body: { texts, target, source: SOURCE_LANG },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  const arr = Array.isArray(data?.translations) ? data.translations : [];
  return texts.map((t, i) => (typeof arr[i] === "string" && arr[i] ? arr[i] : t));
}

export const useAutoTranslate = () => {
  const { i18n } = useTranslation();
  const currentLangRef = useRef<string>(i18n.language);

  useEffect(() => {
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      const lang = (i18n.language || SOURCE_LANG).split("-")[0];

      // If language changed since last tick, restore originals so we can
      // repaint from scratch in the new language.
      if (currentLangRef.current !== lang) {
        currentLangRef.current = lang;
      }

      const nodes = collectTextNodes(document.body);

      // Restore-to-source path
      if (lang === SOURCE_LANG) {
        for (const node of nodes) {
          const orig = originals.get(node);
          if (orig && node.nodeValue !== orig) node.nodeValue = orig;
        }
        return;
      }

      const langCache = getCache(lang);
      const pending = getInFlight(lang);
      const toRequest: string[] = [];
      const seen = new Set<string>();

      for (const node of nodes) {
        // Store original once
        if (!originals.has(node)) originals.set(node, node.nodeValue || "");
        const orig = originals.get(node)!;
        const key = orig.trim();
        if (!key) continue;

        const cached = langCache.get(key);
        if (cached) {
          // Apply cached translation while preserving surrounding whitespace
          const leading = orig.match(/^\s*/)?.[0] || "";
          const trailing = orig.match(/\s*$/)?.[0] || "";
          const desired = leading + cached + trailing;
          if (node.nodeValue !== desired) node.nodeValue = desired;
        } else if (!pending.has(key) && !seen.has(key)) {
          seen.add(key);
          toRequest.push(key);
        }
      }

      if (toRequest.length === 0) return;

      const batch = toRequest.slice(0, MAX_BATCH);
      batch.forEach((k) => pending.add(k));

      try {
        const translated = await translateBatch(batch, lang);
        batch.forEach((k, i) => {
          const v = translated[i];
          if (typeof v === "string" && v.length > 0) langCache.set(k, v);
        });
      } catch (err) {
        console.warn("[auto-translate] batch failed:", err);
      } finally {
        batch.forEach((k) => pending.delete(k));
      }
    };

    // Fire immediately, then every second
    tick();
    const id = window.setInterval(tick, SCAN_INTERVAL_MS);

    // Also re-run right after a language change
    const onLangChange = () => { tick(); };
    i18n.on("languageChanged", onLangChange);

    return () => {
      stopped = true;
      window.clearInterval(id);
      i18n.off("languageChanged", onLangChange);
    };
  }, [i18n]);
};
